import { readFile, writeFile, access } from 'fs/promises';
import { join } from 'path';

/**
 * Manage .gitignore file entries
 */
export class GitignoreManager {
  private basePath: string;
  private gitignorePath: string;

  constructor(basePath: string = process.cwd()) {
    this.basePath = basePath;
    this.gitignorePath = join(basePath, '.gitignore');
  }

  /**
   * Check if .gitignore exists
   */
  async exists(): Promise<boolean> {
    try {
      await access(this.gitignorePath);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Read .gitignore content
   */
  async read(): Promise<string> {
    try {
      return await readFile(this.gitignorePath, 'utf-8');
    } catch {
      return '';
    }
  }

  /**
   * Write .gitignore content
   */
  async write(content: string): Promise<void> {
    await writeFile(this.gitignorePath, content, 'utf-8');
  }

  /**
   * Create a new .gitignore with entries
   */
  async create(entries: string[]): Promise<void> {
    const content = [
      '# AI Agent Memory Files (generated by @mem2/cli)',
      '# Keep MEMORY.md as source of truth',
      ...entries,
      '',
    ].join('\n');

    await this.write(content);
  }

  /**
   * Add entries to existing .gitignore
   */
  async addEntries(entries: string[]): Promise<void> {
    let content = await this.read();

    // Check which entries are already present
    const newEntries = entries.filter((entry) => !this.hasEntry(content, entry));

    if (newEntries.length === 0) {
      return; // All entries already present
    }

    // Ensure content ends with newline
    if (content && !content.endsWith('\n')) {
      content += '\n';
    }

    // Add new entries with header
    content += '\n# AI Agent Memory Files (generated by @mem2/cli)\n';
    content += newEntries.join('\n') + '\n';

    await this.write(content);
  }

  /**
   * Check if .gitignore has a specific entry
   */
  private hasEntry(content: string, entry: string): boolean {
    const lines = content.split('\n').map((line) => line.trim());
    return lines.includes(entry.trim());
  }

  /**
   * Check if .gitignore ignores a specific file
   */
  async isIgnored(filename: string): Promise<boolean> {
    const content = await this.read();
    const lines = content.split('\n').map((line) => line.trim());
    return lines.includes(filename);
  }

  /**
   * Get recommended entries for compiled memory files
   */
  static getRecommendedEntries(): string[] {
    return ['AGENTS.md', 'CLAUDE.md'];
  }
}
